import type { ReactNode } from 'react';
import { tabListKeyDown } from '../ui/tabs';

/**
 * One sub-navigation component for the whole app (card cMgatOnZPBZU).
 *
 * The 2026-10-06 nav ask was three asks with one answer: a screen's sub-menu
 * is a row of TABS at the TOP of the surface, with the selected tab visibly
 * emphasised — not a row of pills parked at the bottom of the panel. Every
 * screen with a sub-nav renders this, so "tabs at the top" cannot drift
 * screen by screen.
 *
 * Semantics are real tabs (role="tablist" / role="tab" / aria-selected), the
 * row scrolls horizontally instead of wrapping (wrapped rows read as pills
 * again), and the active tab takes the Velvet Room emphasis treatment from
 * design/tokens.css (--color-mustard fill, green ink).
 */
export interface SubTabDef {
  key: string;
  label: string;
  /** A sub-view that exists but is not open yet stays visible, dimmed. */
  disabled?: boolean;
}

/**
 * The DOM id of one tab — `{name}-tab-{key}`, unless a caller with existing
 * deep-link / walk ids overrides it. The panel's `aria-labelledby` has to
 * resolve to exactly this string, so both sides ask this function rather than
 * each spelling the pattern out (UX item 6, sol round 1).
 */
export function subTabId(name: string, key: string, idFor?: (k: string) => string | undefined): string {
  return idFor?.(key) ?? `${name}-tab-${key}`;
}

/** The DOM id of the panel a sub-tab controls — `{name}-panel-{key}`. */
export function subPanelId(name: string, key: string): string {
  return `${name}-panel-${key}`;
}

/**
 * The region a sub-tab row switches: a real `tabpanel` labelled by whichever
 * tab is selected, so a screen reader hears "Plan tab panel, Plan" instead of
 * a loose stack of sections.
 *
 * The wrapper must stack its children exactly as the parent did before it
 * existed — hence `sub-view` in styles/app.css, which mirrors the owning
 * container's gap. One panel per screen: it swaps with the active key, so
 * there is never more than one of them in the DOM.
 */
export function SubPanel({
  name,
  tabKey,
  idFor,
  className,
  children,
}: {
  /** Same `name` the row was built with: ids must line up. */
  name: string;
  /** The key of the tab this panel belongs to. */
  tabKey: string;
  /** The caller's id override, same function passed to SubTabs. */
  idFor?: (key: string) => string | undefined;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      id={subPanelId(name, tabKey)}
      role="tabpanel"
      aria-labelledby={subTabId(name, tabKey, idFor)}
      /* The panel is its own tab stop (APG): a text-only or empty section —
         an empty Dates history, the About tab's prose — has nothing focusable
         inside it, so Tab from the selected tab would otherwise skip the very
         region the row claims to control. */
      tabIndex={0}
      className={className ? `sub-view ${className}` : 'sub-view'}
    >
      {children}
    </div>
  );
}

interface SubTabsProps {
  /** DOM id prefix: the list is `{name}-tabs`, each tab `{name}-tab-{key}`. */
  name: string;
  tabs: SubTabDef[];
  active: string;
  onChange: (key: string) => void;
  /** Accessible name for the tablist. */
  label: string;
  /** Extra class on the row (screen-scoped styling hooks). */
  className?: string;
  /** Stick to the top of the panel's scrollport while the list scrolls. */
  sticky?: boolean;
  /** Rendered before the tab labels (vectors, when a screen wants them). */
  iconFor?: (key: string) => ReactNode;
  /** Per-tab DOM id override — callers with existing deep-link / walk ids
   *  keep them instead of the generated `{name}-tab-{key}`. */
  idFor?: (key: string) => string | undefined;
  /** DOM id of the panel the ACTIVE tab controls. Supplied only for the tab
   *  whose panel is actually mounted, so `aria-controls` never points at an
   *  element the app has not rendered (same contract as TabBar's `panelFor`).
   *  Defaults to `subPanelId(name, key)`; return `undefined` to claim none —
   *  a caller that swaps the panel away (the date planner's prefs overlay). */
  panelFor?: (key: string) => string | undefined;
}

export function SubTabs({ name, tabs, active, onChange, label, className, sticky, iconFor, idFor, panelFor }: SubTabsProps) {
  return (
    <div
      className={[
        'sub-tabs',
        sticky ? 'is-sticky' : '',
        className ?? '',
      ].filter(Boolean).join(' ')}
      role="tablist"
      aria-label={label}
      id={`${name}-tabs`}
    >
      {tabs.map(t => {
        const icon = iconFor?.(t.key);
        /* Only the mounted tab names a panel — an idle tab claims nothing. */
        const controls = t.key === active
          ? (panelFor ? panelFor(t.key) : subPanelId(name, t.key))
          : undefined;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={subTabId(name, t.key, idFor)}
            data-tab={t.key}
            aria-selected={t.key === active}
            aria-controls={controls}
            /* Roving tabindex + arrows (UX item 6): the row is one tab stop. */
            tabIndex={t.key === active ? 0 : -1}
            onKeyDown={e =>
              tabListKeyDown(e, el => {
                const key = el.dataset.tab ?? '';
                const next = tabs.find(x => x.key === key);
                if (next && !next.disabled) onChange(key);
              })
            }
            disabled={t.disabled}
            className={`sub-tab${t.key === active ? ' is-active' : ''}`}
            onClick={() => onChange(t.key)}
          >
            {icon && <span className="sub-tab-icon" aria-hidden="true">{icon}</span>}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
